import React from 'react';
import { RouteObject } from 'react-router-dom';
import * as Pages from '../pages';
import * as PATHS from '../constants/service/common/urlConstants';
import AuthorizedAdminRoute from './AuthorizedAdminRoute';
import { ADMIN_LEVEL_TYPE } from '../views/account/admin/common/constants';

// 권한이 필요한 라우트를 위한 헬퍼 함수
const withAuth = (element: React.ReactNode, level?: number): React.ReactNode => (
  <AuthorizedAdminRoute level={level}>{element}</AuthorizedAdminRoute>
);

// AppRouter에 해당하는 라우트 설정
export const appRoutes: RouteObject[] = [
  // 홈 페이지
  { path: PATHS.HOME_URL_PATH, element: <Pages.Home /> },

  /********** 계정 관리 **********/
  { path: PATHS.ACCOUNT_USER_LIST_URL_PATH, element: withAuth(<Pages.AccountUserList />) },
  { path: PATHS.ACCOUNT_USER_DETAIL_URL_PATH, element: withAuth(<Pages.AccountUserDetail />) },
  { path: PATHS.ACCOUNT_USER_RANK_URL_PATH, element: withAuth(<Pages.AccountUserRankList />) },
  { path: PATHS.ACCOUNT_SELLER_LIST_URL_PATH, element: withAuth(<Pages.AccountSellerList />) },

  /********** 브랜드 관리 **********/
  { path: PATHS.BRAND_JOIN_LIST_URL_PATH, element: withAuth(<Pages.BrandJoinList />) },
  { path: PATHS.BRAND_ALL_LIST_URL_PATH, element: withAuth(<Pages.BrandAllList />) },
  { path: PATHS.BRAND_TAG_REQUEST_LIST_URL_PATH, element: withAuth(<Pages.BrandTagRequestList />) },
  { path: PATHS.BRAND_DETAIL_URL_PATH, element: withAuth(<Pages.BrandDetail />) },
  { path: PATHS.BRAND_TIER_URL_PATH, element: withAuth(<Pages.BrandTier />) },
  { path: PATHS.BRAND_TOP_EXPOSURE_URL_PATH, element: withAuth(<Pages.BrandTopExposure />) },
  { path: PATHS.BRAND_EXPOSURE_URL_PATH, element: withAuth(<Pages.BrandExposure />) },

  /********** 관리자 관리 (MASTER 권한) **********/
  { path: PATHS.ACCOUNT_ADMIN_EDIT_URL_PATH, element: withAuth(<Pages.AccountAdminEdit />, ADMIN_LEVEL_TYPE.MASTER) },
  {
    path: PATHS.ACCOUNT_ADMIN_CREATE_URL_PATH,
    element: withAuth(<Pages.AccountAdminCreate />, ADMIN_LEVEL_TYPE.MASTER),
  },
  { path: PATHS.ACCOUNT_ADMIN_LIST_URL_PATH, element: withAuth(<Pages.AccountAdminList />, ADMIN_LEVEL_TYPE.MASTER) },
  {
    path: PATHS.ACCOUNT_ADMIN_DETAIL_URL_PATH,
    element: withAuth(<Pages.AccountAdminDetail />, ADMIN_LEVEL_TYPE.MASTER),
  },

  /********** 상품 관리 **********/
  { path: PATHS.PRODUCT_ALL_LIST_URL_PATH, element: withAuth(<Pages.ProductAllList />) },
  { path: PATHS.PRODUCT_APPROVAL_LIST_URL_PATH, element: withAuth(<Pages.ProductApprovalList />) },
  { path: PATHS.PRODUCT_BAN_LIST_URL_PATH, element: withAuth(<Pages.ProductBanList />) },
  { path: PATHS.PRODUCT_PROPERTY_REQUEST_LIST_URL_PATH, element: withAuth(<Pages.ProductPropertyRequestList />) },
  { path: PATHS.PRODUCT_CREATE_URL_PATH, element: withAuth(<Pages.ProductCreate />) },
  { path: PATHS.PRODUCT_BULK_EDIT_URL_PATH, element: withAuth(<Pages.ProductBulkEdit />) },
  { path: PATHS.PRODUCT_EDIT_URL_PATH, element: withAuth(<Pages.ProductEdit />) },
  { path: PATHS.PRODUCT_DETAIL_URL_PATH, element: withAuth(<Pages.ProductDetail />) },
  { path: PATHS.PRODUCT_VERSION_URL_PATH, element: withAuth(<Pages.ProductVersion />) },
  { path: PATHS.PRODUCT_COPY_URL_PATH, element: withAuth(<Pages.ProductCopy />) },

  /********** 주문 내역 **********/
  { path: PATHS.ORDER_ALL_LIST_URL_PATH, element: withAuth(<Pages.OrderAllList />) },
  { path: PATHS.ORDER_CANCEL_LIST_URL_PATH, element: withAuth(<Pages.OrderCancelList />) },
  { path: PATHS.ORDER_RETURN_LIST_URL_PATH, element: withAuth(<Pages.OrderReturnList />) },
  { path: PATHS.ORDER_EXCHANGE_LIST_URL_PATH, element: withAuth(<Pages.OrderExchangeList />) },
  { path: PATHS.ORDER_DETAIL_URL_PATH, element: withAuth(<Pages.OrderDetail />) },
  { path: PATHS.ORDER_RETURN_DETAIL_URL_PATH, element: withAuth(<Pages.OrderReturnDetail />) },

  /********** 주문/배송 관리 **********/
  { path: PATHS.ORDER_MANAGEMENT_LIST_URL_PATH, element: withAuth(<Pages.OrderManagementList />) },
  { path: PATHS.ORDER_CANCEL_MANAGEMENT_URL_PATH, element: withAuth(<Pages.OrderCancelManagement />) },
  { path: PATHS.ORDER_RETURN_MANAGEMENT_URL_PATH, element: withAuth(<Pages.OrderReturnManagement />) },
  { path: PATHS.ORDER_EXCHANGE_MANAGEMENT_URL_PATH, element: withAuth(<Pages.OrderExchangeManagement />) },

  /********** 멀티채널 관리 **********/
  { path: PATHS.MULTI_CHANNEL_ORDER_MANAGEMENT_URL_PATH, element: withAuth(<Pages.MultiChannelOrderManagement />) },
  { path: PATHS.MULTI_CHANNEL_ORDER_CREATE_URL_PATH, element: withAuth(<Pages.MultiChannelOrderCreate />) },

  /********** 정산 관리 **********/
  { path: PATHS.BALANCE_WAITING_LIST_URL_PATH, element: withAuth(<Pages.BalanceWaitingList />) },
  { path: PATHS.BALANCE_DONE_LIST_URL_PATH, element: withAuth(<Pages.BalanceDoneList />) },
  { path: PATHS.BALANCE_HOLD_LIST_URL_PATH, element: withAuth(<Pages.BalanceHoldList />) },
  { path: PATHS.BALANCE_EXECUTION_URL_PATH, element: withAuth(<Pages.BalanceExecution />) },
  { path: PATHS.BALANCE_BRAND_LIST_URL_PATH, element: withAuth(<Pages.BalanceBrandList />) },
  { path: PATHS.BALANCE_ADJUSTMENT_BASE_DATE_URL_PATH, element: withAuth(<Pages.SettlementDateAdjustment />) },
  {
    path: PATHS.BALANCE_ADJUSTMENT_COMPLETE_DATE_URL_PATH,
    element: withAuth(<Pages.SettlementCompleteDateAdjustment />),
  },

  /********** 결제 관리 **********/
  { path: PATHS.PAYMENT_PG_ROUTE_URL_PATH, element: withAuth(<Pages.PgProviderRoute />) },
  { path: PATHS.PAYMENT_PG_FEE_URL_PATH, element: withAuth(<Pages.PgProviderFee />) },

  /********** 태그 관리 **********/
  { path: PATHS.TAG_CREATE_URL_PATH, element: withAuth(<Pages.TagCreate />) },
  { path: PATHS.TAG_LIST_URL_PATH, element: withAuth(<Pages.TagList />) },
  { path: PATHS.TAG_HISTORY_URL_PATH, element: withAuth(<Pages.TagHistory />) },
  { path: PATHS.TAG_EDIT_URL_PATH, element: withAuth(<Pages.TagEdit />) },
  { path: PATHS.TAG_GROUP_CREATE_URL_PATH, element: withAuth(<Pages.TagGroupCreate />) },
  { path: PATHS.TAG_GROUP_LIST_URL_PATH, element: withAuth(<Pages.TagGroupList />) },
  { path: PATHS.TAG_GROUP_EDIT_URL_PATH, element: withAuth(<Pages.TagGroupEdit />) },
  { path: PATHS.TAG_TAB_MAIN_MANAGEMENT_URL_PATH, element: withAuth(<Pages.TagTabMainManagement />) },
  { path: PATHS.TAG_TREND_KEYWORD_MANAGEMENT_URL_PATH, element: withAuth(<Pages.TagTrendKeywordManagement />) },

  /********** 프로모션 관리 **********/
  { path: PATHS.PROMOTION_SCHEDULE_URL_PATH, element: withAuth(<Pages.PromotionSchedule />) },
  /**
   * ⚠️ **`list` 가 `:promotionScheduleId` 보다 먼저 와야 한다.**
   * 순서가 뒤집히면 `/promotion/schedule/list` 가 id="list" 로 잡혀 상세가 열리고,
   * 리스트 화면이 통째로 사라진다 (백엔드도 같은 이유로 `@Get('list')` 가 앞이다 — AMZBM-110).
   */
  { path: PATHS.PROMOTION_SCHEDULE_LIST_URL_PATH, element: withAuth(<Pages.PromotionScheduleList />) },
  { path: PATHS.PROMOTION_SCHEDULE_DETAIL_URL_PATH, element: withAuth(<Pages.PromotionScheduleDetail />) },

  /********** 기획전 관리 **********/
  { path: PATHS.EVENT_IN_PROGRESS_LIST_URL_PATH, element: withAuth(<Pages.EventInProgressList />) },
  { path: PATHS.EVENT_ALL_LIST_URL_PATH, element: withAuth(<Pages.EventAllList />) },
  { path: PATHS.EVENT_CREATE_URL_PATH, element: withAuth(<Pages.EventCreate />) },
  { path: PATHS.EVENT_EDIT_URL_PATH, element: withAuth(<Pages.EventEdit />) },

  /********** 쿠폰 관리 **********/
  { path: PATHS.COUPON_LIST_URL_PATH, element: withAuth(<Pages.CouponList />) },
  { path: PATHS.COUPON_GENERATE_URL_PATH, element: withAuth(<Pages.CouponGenerate />) },
  { path: PATHS.COUPON_ISSUE_HISTORY_LIST_URL_PATH, element: withAuth(<Pages.CouponIssueHistoryList />) },
  { path: PATHS.COUPON_ISSUE_HISTORY_DETAIL_URL_PATH, element: withAuth(<Pages.CouponIssueHistoryDetail />) },
  { path: PATHS.COUPON_EDIT_URL_PATH, element: withAuth(<Pages.CouponEdit />) },
  { path: PATHS.COUPON_COPY_URL_PATH, element: withAuth(<Pages.CouponCopy />) },
  { path: PATHS.COUPON_DETAIL_URL_PATH, element: withAuth(<Pages.CouponDetail />) },

  { path: PATHS.COUPON_CREATE_URL_PATH, element: withAuth(<Pages.CouponCreate />) },

  /********** 알림톡 발송 **********/
  { path: PATHS.ALIMTALK_SEND_CREATE_URL_PATH, element: withAuth(<Pages.AlimtalkSendCreate />) },
  { path: PATHS.ALIMTALK_SEND_HISTORY_LIST_URL_PATH, element: withAuth(<Pages.AlimtalkSendHistoryList />) },
  { path: PATHS.ALIMTALK_SEND_HISTORY_DETAIL_URL_PATH, element: withAuth(<Pages.AlimtalkSendHistoryDetail />) },

  /********** 포인트 관리 **********/
  { path: PATHS.POINT_LIST_URL_PATH, element: withAuth(<Pages.PointList />) },
  { path: PATHS.POINT_GENERATE_URL_PATH, element: withAuth(<Pages.PointGenerate />) },
  { path: PATHS.POINT_ISSUE_HISTORY_LIST_URL_PATH, element: withAuth(<Pages.PointIssueHistoryList />) },
  { path: PATHS.POINT_ISSUE_HISTORY_DETAIL_URL_PATH, element: withAuth(<Pages.PointIssueHistoryDetail />) },
  { path: PATHS.POINT_RANKING_LIST_URL_PATH, element: withAuth(<Pages.PointRankingList />) },
  { path: PATHS.POINT_REFERRAL_ISSUE_LIST_URL_PATH, element: withAuth(<Pages.PointReferralIssueList />) },
  { path: PATHS.POINT_CAUSE_LIST_URL_PATH, element: withAuth(<Pages.PointCauseList />) },
  { path: PATHS.POINT_CAUSE_CREATE_URL_PATH, element: withAuth(<Pages.PointCauseCreate />) },

  /********** 페이지 관리 **********/
  { path: PATHS.MAIN_TAB_MANAGEMENT_URL_PATH, element: withAuth(<Pages.HomeTabList />) },
  { path: PATHS.MAIN_PAGE_MANAGEMENT_URL_PATH, element: withAuth(<Pages.MainPageWidgetList />) },
  { path: PATHS.SUB_PAGE_LIST_URL_PATH, element: withAuth(<Pages.SubPageList />) },
  { path: PATHS.SUB_PAGE_CREATE_URL_PATH, element: withAuth(<Pages.SubPageCreate />) },
  { path: PATHS.SUB_PAGE_MANAGEMENT_URL_PATH, element: withAuth(<Pages.SubPageWidgetList />) },
  { path: PATHS.WIDGET_ALL_LIST_URL_PATH, element: withAuth(<Pages.WidgetAllList />) },
  { path: PATHS.WIDGET_HOME_MENU_CREATE_URL_PATH, element: withAuth(<Pages.WidgetHomeMenuCreate />) },
  { path: PATHS.WIDGET_CREATE_URL_PATH, element: withAuth(<Pages.WidgetCreate />) },
  { path: PATHS.WIDGET_EDIT_URL_PATH, element: withAuth(<Pages.WidgetEdit />) },
  { path: PATHS.WIDGET_COPY_URL_PATH, element: withAuth(<Pages.WidgetCopy />) },

  /********** 카테고리 관리 **********/
  { path: PATHS.CATEGORY_CREATE_MANAGEMENT_URL_PATH, element: withAuth(<Pages.CategoryCreateManagement />) },
  { path: PATHS.CATEGORY_EXHIBIT_MANAGEMENT_URL_PATH, element: withAuth(<Pages.CategoryExhibitManagement />) },
  { path: PATHS.CATEGORY_DETAIL_URL_PATH, element: withAuth(<Pages.CategoryDetail />) },
  { path: PATHS.CATEGORY_CREATE_URL_PATH, element: withAuth(<Pages.CategoryCreate />) },

  /********** 원격 이미지 관리 **********/
  { path: PATHS.REMOTE_SPLASH_IMAGE_LIST_URL_PATH, element: withAuth(<Pages.RemoteImageSplashList />) },
  { path: PATHS.REMOTE_SPLASH_IMAGE_CREATE_URL_PATH, element: withAuth(<Pages.RemoteImageSplashCreate />) },
  { path: PATHS.REMOTE_SPLASH_IMAGE_EDIT_URL_PATH, element: withAuth(<Pages.RemoteImageSplashEdit />) },
  { path: PATHS.REMOTE_REFERRAL_IMAGE_LIST_URL_PATH, element: withAuth(<Pages.RemoteImageReferralList />) },
  { path: PATHS.REMOTE_REFERRAL_IMAGE_CREATE_URL_PATH, element: withAuth(<Pages.RemoteImageReferralCreate />) },
  { path: PATHS.REMOTE_REFERRAL_IMAGE_EDIT_URL_PATH, element: withAuth(<Pages.RemoteImageReferralEdit />) },

  /********** 공지사항 관리 **********/
  { path: PATHS.NOTICE_STORE_LIST_URL_PATH, element: withAuth(<Pages.NoticeStoreList />) },
  { path: PATHS.NOTICE_STORE_CREATE_URL_PATH, element: withAuth(<Pages.NoticeStoreCreate />) },
  { path: PATHS.NOTICE_STORE_EDIT_URL_PATH, element: withAuth(<Pages.NoticeStoreEdit />) },
  { path: PATHS.NOTICE_PRODUCT_LIST_URL_PATH, element: withAuth(<Pages.NoticeProductList />) },
  { path: PATHS.NOTICE_PRODUCT_DETAIL_URL_PATH, element: withAuth(<Pages.NoticeProductDetail />) },

  /********** 검색 키워드 관리 **********/
  { path: PATHS.SEARCH_WORD_LIST_URL_PATH, element: withAuth(<Pages.SearchWordList />) },
  { path: PATHS.SEARCH_WORD_CREATE_URL_PATH, element: withAuth(<Pages.SearchWordCreate />) },
  { path: PATHS.SEARCH_WORD_EDIT_URL_PATH, element: withAuth(<Pages.SearchWordEdit />) },
  { path: PATHS.SEARCH_DICTIONARY_URL_PATH, element: withAuth(<Pages.SearchDictionary />) },
  { path: PATHS.SEARCH_DICTIONARY_SYNONYM_CREATE_URL_PATH, element: withAuth(<Pages.SearchDictionaryRule />) },
  { path: PATHS.SEARCH_DICTIONARY_SYNONYM_DETAIL_URL_PATH, element: withAuth(<Pages.SearchDictionaryRule />) },

  /********** 시스템 관리 **********/
  { path: PATHS.SYSTEM_FEATURE_FLAG_URL_PATH, element: withAuth(<Pages.FeatureFlag />) },

  /********** 문의 관리 **********/
  { path: PATHS.CS_INQUIRY_LIST_URL_PATH, element: withAuth(<Pages.CsInquiryList />) },
  { path: PATHS.CS_INQUIRY_DETAIL_URL_PATH, element: withAuth(<Pages.CsInquiryDetail />) },
  { path: PATHS.CS_QNA_LIST_URL_PATH, element: withAuth(<Pages.CsQnaList />) },
  { path: PATHS.CS_QNA_DETAIL_URL_PATH, element: withAuth(<Pages.CsQnaDetail />) },
  { path: PATHS.CS_REVIEW_LIST_URL_PATH, element: withAuth(<Pages.CsReviewList />) },
  { path: PATHS.CS_REVIEW_DETAIL_URL_PATH, element: withAuth(<Pages.CsReviewDetail />) },
  { path: PATHS.CS_REVIEW_CREATE_URL_PATH, element: withAuth(<Pages.CsReviewCreate />) },

  /********** 버전 관리 **********/
  { path: PATHS.VERSION_LIST_URL_PATH, element: withAuth(<Pages.VersionList />) },
  { path: PATHS.VERSION_CREATE_URL_PATH, element: withAuth(<Pages.VersionCreate />) },
  { path: PATHS.VERSION_EDIT_URL_PATH, element: withAuth(<Pages.VersionEdit />) },

  /********** 추천 관리 **********/
  { path: PATHS.RECOMMEND_PRODUCT_LIST_URL_PATH, element: withAuth(<Pages.RecommendProductList />) },
  { path: PATHS.RECOMMEND_PRODUCT_DETAIL_URL_PATH, element: withAuth(<Pages.RecommendProductDetail />) },
  { path: PATHS.RECOMMEND_PRODUCT_COPY_URL_PATH, element: withAuth(<Pages.RecommendProductCopy />) },
  { path: PATHS.RECOMMEND_PRODUCT_CREATE_URL_PATH, element: withAuth(<Pages.RecommendProductCreate />) },
  { path: PATHS.RECOMMEND_REVIEW_LIST_URL_PATH, element: withAuth(<Pages.RecommendReviewList />) },
  { path: PATHS.RECOMMEND_REVIEW_DETAIL_URL_PATH, element: withAuth(<Pages.RecommendReviewDetail />) },
  { path: PATHS.RECOMMEND_REVIEW_COPY_URL_PATH, element: withAuth(<Pages.RecommendReviewCopy />) },
  { path: PATHS.RECOMMEND_REVIEW_CREATE_URL_PATH, element: withAuth(<Pages.RecommendReviewCreate />) },
  { path: PATHS.RECOMMEND_TAG_LIST_URL_PATH, element: withAuth(<Pages.RecommendTagList />) },
  { path: PATHS.RECOMMEND_TAG_DETAIL_URL_PATH, element: withAuth(<Pages.RecommendTagDetail />) },
  { path: PATHS.RECOMMEND_TAG_COPY_URL_PATH, element: withAuth(<Pages.RecommendTagCopy />) },
  { path: PATHS.RECOMMEND_TAG_CREATE_URL_PATH, element: withAuth(<Pages.RecommendTagCreate />) },
  { path: PATHS.RECOMMEND_SEARCH_WORD_LIST_URL_PATH, element: withAuth(<Pages.RecommendSearchWordList />) },
  { path: PATHS.RECOMMEND_SEARCH_WORD_DETAIL_URL_PATH, element: withAuth(<Pages.RecommendSearchWordDetail />) },
  { path: PATHS.RECOMMEND_SEARCH_WORD_COPY_URL_PATH, element: withAuth(<Pages.RecommendSearchWordCopy />) },
  { path: PATHS.RECOMMEND_SEARCH_WORD_CREATE_URL_PATH, element: withAuth(<Pages.RecommendSearchWordCreate />) },
  { path: PATHS.RECOMMEND_EVENT_LIST_URL_PATH, element: withAuth(<Pages.RecommendEventList />) },
  { path: PATHS.RECOMMEND_EVENT_DETAIL_URL_PATH, element: withAuth(<Pages.RecommendEventDetail />) },
  { path: PATHS.RECOMMEND_EVENT_COPY_URL_PATH, element: withAuth(<Pages.RecommendEventCopy />) },
  { path: PATHS.RECOMMEND_EVENT_CREATE_URL_PATH, element: withAuth(<Pages.RecommendEventCreate />) },
  { path: PATHS.RECOMMEND_STORE_LIST_URL_PATH, element: withAuth(<Pages.RecommendStoreList />) },
  { path: PATHS.RECOMMEND_STORE_DETAIL_URL_PATH, element: withAuth(<Pages.RecommendStoreDetail />) },
  { path: PATHS.RECOMMEND_STORE_COPY_URL_PATH, element: withAuth(<Pages.RecommendStoreCopy />) },
  { path: PATHS.RECOMMEND_STORE_CREATE_URL_PATH, element: withAuth(<Pages.RecommendStoreCreate />) },

  /********** 응모하기 **********/
  { path: PATHS.LUCKY_DRAW_URL_PATH, element: withAuth(<Pages.LuckyDraws />) },

  /********** 에러 페이지 **********/
  { path: PATHS.INVALID_REQUEST_URL_PATH, element: <Pages.NotFound /> },
  { path: PATHS.UNAUTHORIZED_REQUEST_URL_PATH, element: <Pages.NotAuthorized /> },
  { path: '*', element: <Pages.NotFound /> },
];
